import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/synapse/Icon';
import { Field } from '@/components/synapse/Field';
import { Select } from '@/components/synapse/Select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { translateApiError } from '@/lib/apiError';
import { translateSpecimenType } from '@/lib/labTests';
import {
  useCreateCatalogueEntry,
  useLabCatalogue,
  useUpdateCatalogueEntry,
} from './useLab';
import { freeTextCode, SPECIMEN_TYPES, type SpecimenType } from '@synapse/lab-tests';
import type { LabTestDefinitionDTO } from '@/types/lab';

/**
 * The clinic's test list: the built-in presets, plus whatever this clinic added
 * or changed.
 *
 * The merge rule is the thing to hold on to. There is one list, keyed by code,
 * and a clinic row of the same code REPLACES the preset. That single rule covers
 * both jobs a clinic actually has — "add a test we do" and "hide or rename one
 * we don't" — without a second concept for overrides.
 *
 * Which means editing a preset is not an edit at all: it writes a clinic row
 * carrying that preset's code. The screen says so rather than hiding it, because
 * the consequence is visible later — the preset stops tracking any future change
 * to the built-in list.
 */
export function TestListSection() {
  const { t } = useTranslation();

  // The management view, unlike ordering, must see what was switched off — a
  // list that hides its own hidden entries cannot un-hide them.
  const catalogue = useLabCatalogue({ includeInactive: true });
  const create = useCreateCatalogueEntry();
  const update = useUpdateCatalogueEntry();

  const [error, setError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftCode, setDraftCode] = useState('');
  const [draftType, setDraftType] = useState<SpecimenType>('blood');
  const [draftContainer, setDraftContainer] = useState('');

  const entries = catalogue.data?.tests ?? [];
  const suggestions = catalogue.data?.pastFreeTextNames ?? [];

  const grouped = useMemo(
    () =>
      SPECIMEN_TYPES.map((type) => ({
        type,
        entries: entries.filter((e) => e.specimenType === type),
      })).filter((g) => g.entries.length > 0),
    [entries],
  );

  const existingCodes = useMemo(
    () => new Set(entries.map((e) => e.code.toLowerCase())),
    [entries],
  );

  const trimmedCode = draftCode.trim();
  const duplicate = trimmedCode !== '' && existingCodes.has(trimmedCode.toLowerCase());

  async function addEntry() {
    setError(null);
    const name = draftName.trim();
    const code = trimmedCode;
    if (!name || !code) {
      setError(t('lab.catalogue.errors.incomplete'));
      return;
    }
    if (duplicate) {
      setError(t('lab.catalogue.errors.duplicate'));
      return;
    }
    try {
      const container = draftContainer.trim();
      await create.mutateAsync({
        code,
        name,
        specimenType: draftType,
        ...(container ? { container } : {}),
      });
      setDraftName('');
      setDraftCode('');
      setDraftContainer('');
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.catalogue.errors.saveFailed') }));
    }
  }

  /**
   * Switch an entry on or off.
   *
   * A preset has no row to update, so hiding one means WRITING a clinic row that
   * carries its code and is inactive — the same merge rule the list is built on,
   * used deliberately rather than worked around.
   */
  async function setActive(entry: LabTestDefinitionDTO, active: boolean) {
    setError(null);
    try {
      if (entry.id) {
        await update.mutateAsync({ id: entry.id, input: { active } });
      } else {
        await create.mutateAsync({
          code: entry.code,
          name: entry.name,
          specimenType: entry.specimenType,
          ...(entry.container ? { container: entry.container } : {}),
          active,
        });
      }
    } catch (err) {
      setError(translateApiError(err, { fallback: t('lab.catalogue.errors.saveFailed') }));
    }
  }

  /** Prefill the add form from a name the clinic has typed on past orders. */
  function promote(name: string) {
    setDraftName(name);
    // `freeTextCode` produces `free:<slug>`; the catalogue wants a real code, so
    // the slug is offered as a starting point for the operator to shorten.
    setDraftCode(freeTextCode(name).replace('free:', '').toUpperCase().slice(0, 16));
  }

  const busy = create.isPending || update.isPending;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-[13px] text-muted">{t('lab.catalogue.subtitle')}</p>

      {error && (
        <p role="alert" className="text-[13px] text-destructive">
          {error}
        </p>
      )}

      <section className="flex flex-col gap-2 rounded-md border border-line-2 bg-paper px-3.5 py-3">
        <h2 className="text-[13px] font-semibold text-ink">{t('lab.catalogue.addTitle')}</h2>
        {/* Top-aligned: the code field's hint/error hangs below the row, so
            bottom alignment would shove that control out of line with the rest. */}
        <div className="flex flex-wrap items-start gap-2">
          <Field label={t('lab.catalogue.name')} className="min-w-[200px] flex-1">
            {(id) => (
              <Input id={id} value={draftName} onChange={(e) => setDraftName(e.target.value)} />
            )}
          </Field>
          <Field
            label={t('lab.catalogue.code')}
            className="w-36"
            hint={t('lab.catalogue.codeHint')}
            error={duplicate ? t('lab.catalogue.errors.duplicate') : undefined}
          >
            {(id) => (
              <Input
                id={id}
                value={draftCode}
                onChange={(e) => setDraftCode(e.target.value.toUpperCase())}
              />
            )}
          </Field>
          <Field label={t('lab.catalogue.specimen')} className="w-36">
            {(id) => (
              <Select
                id={id}
                value={draftType}
                onChange={(e) => setDraftType(e.target.value as SpecimenType)}
              >
                {SPECIMEN_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {translateSpecimenType(t, type)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('lab.catalogue.container')} className="w-40" optional>
            {(id) => (
              <Input
                id={id}
                value={draftContainer}
                onChange={(e) => setDraftContainer(e.target.value)}
              />
            )}
          </Field>
          <div className="flex flex-col gap-1.5">
            {/* Blank stand-in for a Field label, so the button lines up with the inputs. */}
            <span aria-hidden className="text-xs">
              &nbsp;
            </span>
            <Button onClick={() => void addEntry()} disabled={busy} className="h-10">
              {t('common.add')}
            </Button>
          </div>
        </div>
      </section>

      {suggestions.length > 0 && (
        <section className="flex flex-col gap-2">
          <h2 className="text-[12px] font-medium text-muted">
            {t('lab.catalogue.suggestionsTitle')}
          </h2>
          <p className="text-[12.5px] text-muted">{t('lab.catalogue.suggestionsHint')}</p>
          <ul className="flex flex-wrap gap-2">
            {suggestions.map((name) => (
              <li key={name}>
                <button
                  type="button"
                  onClick={() => promote(name)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-dashed border-line-2 px-3 text-[12.5px] text-muted hover:border-line hover:text-ink"
                >
                  <Icon name="plus" size={12} />
                  {name}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {catalogue.isPending && (
        <div className="h-32 animate-pulse rounded-md border border-line-2 bg-paper-2" />
      )}

      {catalogue.isError && (
        <div className="rounded-md border border-line-2 bg-paper px-4 py-6 text-center">
          <p className="text-[13.5px] text-ink">{t('lab.new.catalogueFailed')}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void catalogue.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      )}

      {grouped.map((group) => (
        <section key={group.type} className="flex flex-col gap-2">
          <h2 className="text-[12px] font-medium uppercase tracking-[0.04em] text-muted">
            {translateSpecimenType(t, group.type)}
          </h2>
          <ul className="flex flex-col gap-1.5">
            {group.entries.map((entry) => (
              <li key={entry.code}>
                <CatalogueRow
                  entry={entry}
                  busy={busy}
                  onToggle={(active) => void setActive(entry, active)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function CatalogueRow({
  entry,
  busy,
  onToggle,
}: {
  entry: LabTestDefinitionDTO;
  busy: boolean;
  onToggle: (active: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      className={
        'flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line-2 bg-paper px-3 py-2.5 ' +
        (entry.active ? '' : 'opacity-60')
      }
    >
      <span className="min-w-[160px] flex-1 text-[13.5px] text-ink">{entry.name}</span>

      <span className="font-mono text-[12px] tabular-nums text-muted">{entry.code}</span>

      {entry.container && <span className="text-[12px] text-muted">{entry.container}</span>}

      {/* Provenance is shown because it changes what happens next: editing a
          preset writes a clinic row that stops tracking the built-in list. */}
      <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted">
        {t(entry.id ? 'lab.catalogue.custom' : 'lab.catalogue.preset')}
      </span>

      {!entry.active && (
        <span className="rounded-full border border-line-2 px-2 py-0.5 text-[11px] text-muted">
          {t('lab.catalogue.hidden')}
        </span>
      )}

      <Button
        size="sm"
        variant="secondary"
        disabled={busy}
        onClick={() => onToggle(!entry.active)}
        aria-label={t(entry.active ? 'lab.catalogue.hideTest' : 'lab.catalogue.showTest', {
          test: entry.name,
        })}
      >
        {t(entry.active ? 'lab.catalogue.hide' : 'lab.catalogue.show')}
      </Button>
    </div>
  );
}
